import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Modal from "@intility/bifrost-react/Modal";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import { useCallback, useEffect, useState } from "react";
import type { GrantProviderReadersResult } from "~/api/clients";
import { useGrantKeycloakViewers } from "~/api/clients";
import type { Tuple } from "~/api/fga-types";
import {
  deleteManagementTuple,
  readManagementTuples,
  writeManagementTuple,
} from "~/api/management";
import { useCapabilities, useIsKeycloakModuleOwner } from "~/auth/capabilities";
import { PageLayout } from "~/components/layout/PageLayout";

// Small operator page for the OpenFGA **management** store — access-over-access
// for the Keycloak and OpenFGA modules. Deliberately minimal: list the grants
// and add/remove them. The access store (real authorization data) is managed
// from the Authorization pages; this is the separate meta store.
export default function ManagementStore() {
  const { can } = useCapabilities();
  const canWrite = can("governance.write");
  const { isKeycloakModuleOwner } = useIsKeycloakModuleOwner();
  const [tuples, setTuples] = useState<Tuple[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [user, setUser] = useState("");
  const [relation, setRelation] = useState("");
  const [object, setObject] = useState("");

  const [showGrantKcModal, setShowGrantKcModal] = useState(false);
  const [grantKcResult, setGrantKcResult] =
    useState<GrantProviderReadersResult | null>(null);
  const grantKcMutation = useGrantKeycloakViewers();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Follow every page: the grants table must show the whole store, not
      // just the first page (the bulk "Grant Keycloak viewers" action can
      // write far more tuples than one page holds).
      const all: Tuple[] = [];
      let token: string | undefined;
      do {
        const res = await readManagementTuples({
          allowUnfiltered: true,
          pageSize: 100,
          continuationToken: token,
        });
        all.push(...res.tuples);
        token = res.continuationToken || undefined;
      } while (token);
      setTuples(all);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = () => {
    if (!user || !relation || !object) return;
    setError(null);
    writeManagementTuple({ user, relation, object })
      .then(() => {
        setUser("");
        setRelation("");
        setObject("");
        load();
      })
      .catch((err) => setError(err.message));
  };

  const remove = (t: Tuple) => {
    setError(null);
    deleteManagementTuple(t.key)
      .then(load)
      .catch((err) => setError(err.message));
  };

  return (
    <PageLayout
      title="Management Store"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Management" },
        { label: "Tuples" },
      ]}
      description="Access-over-access: who may administer the Keycloak and OpenFGA modules. This is the separate management store — the access store (authorization data) is managed from the Authorization pages."
    >
      {error && (
        <Message state="alert" header="Error">
          {error}
        </Message>
      )}

      {canWrite && (
        <Section>
          <Section.Header>Grant access</Section.Header>
          <Section.Content>
            <Grid cols={1} small={3} gap={12}>
              <Input
                label="User"
                placeholder="user:<uuid>"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                required
              />
              <Input
                label="Relation"
                placeholder="owner"
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                required
              />
              <Input
                label="Object"
                placeholder="module:keycloak"
                value={object}
                onChange={(e) => setObject(e.target.value)}
                required
              />
            </Grid>
          </Section.Content>
          <Section.Content borderTop>
            <Button onClick={add} disabled={!user || !relation || !object}>
              Grant
            </Button>
          </Section.Content>
        </Section>
      )}

      {isKeycloakModuleOwner && (
        <Section>
          <Section.Content>
            <Button
              variant="flat"
              onClick={() => {
                setGrantKcResult(null);
                setShowGrantKcModal(true);
              }}
            >
              Grant Keycloak viewers
            </Button>
          </Section.Content>
        </Section>
      )}

      <Section>
        <Section.Header>Grants</Section.Header>
        <Table noBorder>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>User</Table.HeaderCell>
              <Table.HeaderCell>Relation</Table.HeaderCell>
              <Table.HeaderCell>Object</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tuples.map((t) => {
              const key = `${t.key.user}|${t.key.relation}|${t.key.object}`;
              return (
                <Table.Row key={key}>
                  <Table.Cell>{t.key.user}</Table.Cell>
                  <Table.Cell>{t.key.relation}</Table.Cell>
                  <Table.Cell>{t.key.object}</Table.Cell>
                  <Table.Cell>
                    {canWrite && (
                      <Button small variant="flat" onClick={() => remove(t)}>
                        Revoke
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
            {!loading && tuples.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4}>
                  No grants in the management store.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Section>

      {showGrantKcModal && (
        <Modal
          isOpen
          header="Grant Keycloak viewers"
          onRequestClose={() => setShowGrantKcModal(false)}
        >
          {grantKcResult ? (
            <>
              <Message
                state={grantKcResult.failed > 0 ? "alert" : "success"}
                header="Result"
              >
                <p>
                  Granted: {grantKcResult.granted} |{" "}
                  <span style={{ color: "var(--bfc-status-warning)" }}>
                    Unchanged: {grantKcResult.unchanged}
                  </span>{" "}
                  | Skipped: {grantKcResult.skipped} | Failed:{" "}
                  {grantKcResult.failed} | Total: {grantKcResult.total}
                </p>
                {grantKcResult.unchanged > 0 && (
                  <p>
                    Unchanged means the user already had this role. No change
                    was made for them.
                  </p>
                )}
                {grantKcResult.failed > 0 && (
                  <p>
                    Failed means a rule refused that member. The other members
                    were granted. It is safe to run this again.
                  </p>
                )}
                {grantKcResult.errors.length > 0 && (
                  <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                    {grantKcResult.errors.slice(0, 10).map((e) => (
                      <li key={e} style={{ fontSize: 12 }}>
                        {e}
                      </li>
                    ))}
                    {grantKcResult.errors.length > 10 && (
                      <li style={{ fontSize: 12 }}>
                        …and {grantKcResult.errors.length - 10} more
                      </li>
                    )}
                  </ul>
                )}
              </Message>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Button onClick={() => setShowGrantKcModal(false)}>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="bf-p">
                This will grant <strong>viewer</strong> on{" "}
                <code>module:keycloak</code> to all members of the Intility
                organization. It is safe to run again.
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Button
                  variant="flat"
                  onClick={() => setShowGrantKcModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={grantKcMutation.isPending}
                  onClick={() => {
                    grantKcMutation.mutate(undefined, {
                      onSuccess: (result) => {
                        setGrantKcResult(result);
                        load();
                      },
                      // The request itself failed, so the server's summary never
                      // arrived. The run is one request now (ADR-0047), so it was
                      // almost certainly never judged, but the activity log is the
                      // authority on what landed.
                      onError: (err) =>
                        setGrantKcResult({
                          total: 0,
                          granted: 0,
                          unchanged: 0,
                          skipped: 0,
                          failed: 1,
                          errors: [err.message],
                        }),
                    });
                  }}
                >
                  {grantKcMutation.isPending ? "Granting…" : "Grant viewers"}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </PageLayout>
  );
}
